import gql from "graphql-tag";
import { useQuery, useLazyQuery } from "@apollo/client";
import { apolloClient } from "../apolloClient";
import { Store } from "../store";

export const usePlaylist = (options) => {
  const [getPlaylist, { data, loading }] = useLazyQuery(GET_PLAYLIST, { variables: options });
  const playlist = data?.playlist;

  return { getPlaylist, playlist, loading };
};

export const useCollection = (options) => {
  const [getCollection, { data, loading }] = useLazyQuery(GET_COLLECTION, { variables: options });
  const collection = data?.collection;

  return { getCollection, collection, loading };
};

export const useDefaultImage = () => {
  const config = Store.useState(s => s.config);

  return config.getProperty("designs", "missingImageUri");
};

const itemLoader = async (id) => {
  return apolloClient.query({ query: GET_ITEM, variables: { id } });
};

const collectionLoader = async (id) => {
  return apolloClient.query({ query: GET_COLLECTION, variables: { id } });
};

const searchLoader = async (term) => {
  return apolloClient.query({ query: GET_SEARCH_ITEM, variables: { term } });
};

const useInventory = (options = {}) => {
  const { data, loading, error } = useQuery(GET_INVENTORY, { variables: options });

  return { data, loading, error };
};

const useSuggestions = (options = {}) => {
  const { data, loading, error } = useQuery(GET_ITEM_SUGGESTIONS, { variables: options });

  return { data, loading, error };
};

export { itemLoader, collectionLoader, useInventory, useSuggestions, searchLoader };

export const ITEM = `
  _id
  id
  type
  path
  wanted
  uri
  isForbiddenForSale
  listings {
    id
    available
    preOrder
    secondHand
    stock {
      quantity
    }
    prices {
        beforeTaxes
        sale
        compare
    }
    onePerCustomer
    options {
        name
        value
    }
    comments
  }
  descriptions {
    main
    shop {
        text
        html
        short
    }
  }
  data {
    title
    images {
        uri
        alternative {
          uri
        }
    }
    assetLink
    weight
    genres
    styles
    manufacturer
    cat
    releaseDate
    tracklist {
      type_
      title
      artists {
        name
        id
        anv
      }
      uri
      duration
      position
    }
    formats {
      descriptions
      name
      qty
    }
    country
    artists {
      name
      anv
      id
    }
    labels {
      name
      id
      catno
    }
    videos {
      uri
    }

    subtitle
    authors
    publisher
    publishedDate
    industryIdentifiers {
      type
      identifier
    }
    pageCount
    categories
    maturityRating
    language
  }
`;

const PLAYLIST = `
  _id
  id
  title
  link
  entries {
    item {
      ${ITEM}
    }
    comments
  }
`;

const COLLECTION_WITH_DATA = `
  collection {
    id
    title
    handle
    viewType
    description {
      content
    }
    sort {
      key
      order
    }
    banner {
      media {
        _id
        url
        formats {
          banner {
            url
            name
          }
          square {
            url
            name
          }
          small {
            url
          }
        }
      }
      format
    }
  }
  page {
    pagination {
      limit
      pages
      page
      sort
      order
    }
    items {
      ${ITEM}
    }
  }
`;

export const GET_PLAYLIST = gql(`
  query($id: Int!) {
    playlist(id: $id) {
      ${PLAYLIST}
    }
  }
`);

export const GET_COLLECTION = gql(`
  query($handle: String, $id: Int, $page: Int, $limit: Int, $order: Int, $sort: String, $randomise: Boolean) {
    collection(handle: $handle, id: $id, page: $page, limit: $limit, order: $order, sort: $sort, randomise: $randomise) {
      ${COLLECTION_WITH_DATA}
    }
  }
`);

export const GET_ITEM = gql(`
  query($id: Float!) {
    item(id: $id) {
      ${ITEM}
    }
}`);

export const GET_ITEMS = gql(`
  query($ids: [Float]!) {
    items(ids: $ids) {
      ${ITEM}
    }
  }
`);

export const GET_SEARCH_ITEM = gql(`
  query($term: String!) {
    search(term: $term) {
      items {
        ${ITEM}
      }
    }
}`);

const INVENTORY = `
  pagination {
    limit
    pages
    page
  }
  filters
  items {
    ${ITEM}
  }
`;

export const GET_INVENTORY = gql(`
  query($wantlist: Boolean, $to: String, $preorder: Boolean, $from: String, $page: Int, $types: [String], $manufacturers: [String], $type: String! $maxPrice: Int, $countries: [String], $years: [Int], $artists: [Float], $labels: [Float], $formats: [String], $categories: [String], $stock: String, $condition: String, $order: Int, $sort: String, $limit: Int, $styles: [String], $genres: [String], $mediums: [String]) {
    inventory(wantlist: $wantlist, to: $to, from: $from, preorder: $preorder, page: $page, types: $types, manufacturers: $manufacturers, type: $type, maxPrice: $maxPrice, countries: $countries, years: $years, artists: $artists, labels: $labels, formats: $formats, categories: $categories, stock: $stock, condition: $condition, order: $order, sort: $sort, styles: $styles, genres: $genres, limit: $limit, mediums: $mediums) {
      ${INVENTORY}
    }
  }
`);

export const GET_ITEM_SUGGESTIONS = gql(`
  query($id: Float, $type: String, $genres: [String], $styles: [String], $categories: [String], $country:String, $limit: Int) {
    suggestions(id: $id, type: $type, genres: $genres, styles: $styles, categories: $categories, country: $country, limit: $limit) {
        ${ITEM}
    }
}`);

const ARTIST = `
  id
  name
`;

const LABEL = `
  id
  name
  images {
    uri
  }
`;

export const GET_ARTISTS = gql(`
  query {
    artists {
      ${ARTIST}
    }
}`);

export const GET_LABELS = gql(`
  query($letter: String) {
    labels(letter: $letter) {
        ${LABEL}
    }
}`);

export const POST_RELEASE_SNIPPET_ARCHIVE = gql(`
  mutation($id: Float!) {
    itemSnippetsArchive(id: $id)
}`);
